// src/pages/RegistroNotePage.js
// ═══════════════════════════════════════════════════════════════════════════
// REGISTRO DELLE MIE NOTE (ottobre 2026)
// ═══════════════════════════════════════════════════════════════════════════
// Tutti i propri post-it in un'unica pagina (barra laterale → Personale):
//  • raggruppati per commessa, con in cima le «Note generali» (senza commessa);
//  • filtro per commessa, ricerca nel testo, archiviate a richiesta;
//  • sono le STESSE note di «📝 Le mie note» nella commessa (collezione noteAppunti): quello
//    che si cambia qui si ritrova là e viceversa;
//  • «Nuova nota» chiede dove scriverla (generale o una commessa); «Sposta in…» porta una
//    nota da un posto all'altro (regole 58).
// Privacy come sempre: ognuno legge e scrive solo le proprie (regole: match /noteAppunti).
// Fuori da ricerca globale, registro eventi, Insight, notifiche ed esportazioni.
// ═══════════════════════════════════════════════════════════════════════════
import React, { useEffect, useMemo, useRef, useState } from "react";
import { collection, query, where, onSnapshot, addDoc, updateDoc, deleteDoc, doc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../contexts/AuthContext";
import { useOrg } from "../contexts/OrgContext";
import { useToast } from "../utils/Toast";
import { confirm } from "../utils/confirm";
import { commessaVisibile } from "../utils/condivisione";
import { PostIt, ordina, ms } from "../components/NoteCommessa";
import { Plus, Lock, Search } from "lucide-react";

const GENERALE = "__generale";
const chiaveDi = n => n.projectId || GENERALE;
const minuscolo = t => String(t || "").toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");

export default function RegistroNotePage({ onNavigate }) {
  const { currentUser, isOrgAdmin, isPlatformOwner } = useAuth();
  const { effectiveOrgId } = useOrg();
  const { addToast } = useToast();
  const uid = currentUser?.uid;
  const [note, setNote] = useState([]);
  const [caricate, setCaricate] = useState(false);
  const [progetti, setProgetti] = useState([]);
  const [filtro, setFiltro] = useState("");          // "" = tutte, GENERALE, oppure id commessa
  const [cerca, setCerca] = useState("");
  const [vediArchiviate, setVediArchiviate] = useState(false);
  const [dove, setDove] = useState("");              // dove scrivere la nuova nota
  const [nuovaId, setNuovaId] = useState(null);
  const pulite = useRef(false);

  useEffect(() => {
    if (!uid || !effectiveOrgId) return undefined;
    // Il filtro per uid è obbligatorio: le regole rifiutano qualsiasi lettura di note altrui
    const q = query(collection(db, "noteAppunti"), where("uid", "==", uid), where("orgId", "==", effectiveOrgId));
    return onSnapshot(q, s => { setNote(s.docs.map(d => ({ id: d.id, ...d.data({ serverTimestamps: "estimate" }) }))); setCaricate(true); },
      () => { setNote([]); setCaricate(true); });
  }, [uid, effectiveOrgId]);

  useEffect(() => {
    if (!effectiveOrgId) return undefined;
    return onSnapshot(query(collection(db, "projects"), where("organizationId", "==", effectiveOrgId)),
      s => setProgetti(s.docs.map(d => ({ id: d.id, ...d.data() }))), () => setProgetti([]));
  }, [effectiveOrgId]);

  // All'apertura le note lasciate vuote da più di un minuto si tolgono da sole (come nella commessa)
  useEffect(() => {
    if (!caricate || pulite.current) return;
    pulite.current = true;
    const limite = Date.now() - 60000;
    note.filter(n => !(n.testo || "").trim() && !n.archiviata && ms(n.createdAt) && ms(n.createdAt) < limite)
      .forEach(n => deleteDoc(doc(db, "noteAppunti", n.id)).catch(() => {}));
  }, [caricate, note]);

  const perId = useMemo(() => Object.fromEntries(progetti.map(p => [p.id, p])), [progetti]);
  // Commesse in cui si può scrivere o spostare una nota: quelle che si vedono, aperte e non archiviate
  const commesseScrivibili = useMemo(() => progetti
    .filter(p => !p.isParent && !p.archived && p.statoCommessa !== "chiusa" && commessaVisibile(p, { uid, isAdmin: isOrgAdmin || isPlatformOwner }))
    .sort((a, b) => etichetta(a).localeCompare(etichetta(b), "it")), [progetti, uid, isOrgAdmin, isPlatformOwner]);

  const nomeGruppo = (chiave, campione) => nomeGruppoDi(chiave, campione, perId);
  const statoGruppo = chiave => {
    if (chiave === GENERALE) return null;
    const p = perId[chiave];
    if (!p) return "non più disponibile";
    if (p.statoCommessa === "chiusa") return "chiusa";
    if (p.archived) return "archiviata";
    return null;
  };

  // Gruppi: in cima le generali, poi le commesse dalla nota modificata più di recente
  const gruppi = useMemo(() => {
    const t = minuscolo(cerca.trim());
    const visibili = note.filter(n => (vediArchiviate || !n.archiviata) && (!filtro || chiaveDi(n) === filtro) &&
      (!t || minuscolo(n.testo).includes(t) || minuscolo(n.projectName).includes(t) || minuscolo(n.codiceCommessa).includes(t)));
    const m = new Map();
    visibili.forEach(n => { const k = chiaveDi(n); if (!m.has(k)) m.set(k, []); m.get(k).push(n); });
    const lista = [...m.entries()].map(([chiave, elenco]) => ({
      chiave, note: elenco.sort((a, b) => (a.archiviata ? 1 : 0) - (b.archiviata ? 1 : 0) || ordina(a, b)),
      recente: Math.max(...elenco.map(n => ms(n.updatedAt) || ms(n.createdAt))),
    }));
    return lista.sort((a, b) => (a.chiave === GENERALE ? -1 : b.chiave === GENERALE ? 1 : b.recente - a.recente));
  }, [note, filtro, cerca, vediArchiviate]);

  // Commesse da proporre nel filtro: quelle che hanno note (anche archiviate)
  const opzioniFiltro = useMemo(() => {
    const chiavi = [...new Set(note.map(chiaveDi))].filter(k => k !== GENERALE);
    return chiavi.map(k => ({ k, nome: nomeGruppoDi(k, note.find(n => chiaveDi(n) === k), perId) })).sort((a, b) => a.nome.localeCompare(b.nome, "it"));
  }, [note, perId]);

  const conTesto = note.filter(n => !n.archiviata && (n.testo || "").trim()).length;
  const doveScelto = dove || (filtro && filtro !== GENERALE && commesseScrivibili.some(p => p.id === filtro) ? filtro : filtro === GENERALE ? GENERALE : "");

  const errore = e => addToast("Nota non salvata: " + (e?.message || e), "error");
  const campiPosto = chiave => {
    if (chiave === GENERALE) return { projectId: "", projectName: "", codiceCommessa: "" };
    const p = perId[chiave] || {};
    return { projectId: chiave, projectName: p.name || "", codiceCommessa: p.codiceCommessa || "" };
  };

  const nuova = async () => {
    if (!doveScelto) { addToast("Scegli prima dove scrivere la nota: generale o in una commessa", "info"); return; }
    const vuota = note.find(n => !n.archiviata && !(n.testo || "").trim() && chiaveDi(n) === doveScelto);
    if (vuota) { setNuovaId(null); setTimeout(() => setNuovaId(vuota.id), 0); return; }
    try {
      const ref = await addDoc(collection(db, "noteAppunti"), {
        uid, orgId: effectiveOrgId, ...campiPosto(doveScelto), testo: "", colore: "giallo", fissata: false, archiviata: false,
        createdAt: serverTimestamp(), updatedAt: serverTimestamp(),
      });
      setNuovaId(ref.id);
      if (filtro && filtro !== doveScelto) setFiltro("");
      setCerca("");
    } catch (e) { errore(e); }
  };
  const salvaTesto = (n, testo) => updateDoc(doc(db, "noteAppunti", n.id), { testo, updatedAt: serverTimestamp() }).catch(errore);
  const aggiorna = (n, campi) => updateDoc(doc(db, "noteAppunti", n.id), { ...campi, updatedAt: serverTimestamp() }).catch(errore);
  const elimina = async n => {
    if ((n.testo || "").trim() && !await confirm("Eliminare questa nota? Non si potrà recuperare.", { type: "danger", title: "Elimina nota", confirmLabel: "Elimina" })) return;
    deleteDoc(doc(db, "noteAppunti", n.id)).catch(errore);
  };
  const sposta = (n, chiave) => {
    if (!chiave || chiave === chiaveDi(n)) return;
    aggiorna(n, campiPosto(chiave)).then(() => addToast(`Nota spostata in ${chiave === GENERALE ? "Note generali" : nomeGruppo(chiave)}`, "success"));
  };

  const selStile = { padding: "7px 10px", borderRadius: 8, border: "1px solid #e5e7eb", fontSize: 13, background: "#fff", fontFamily: "'Inter',sans-serif", color: "#111827" };

  return (
    <div style={{ height: "100%", overflowY: "auto", background: "#f9fafb" }}>
      <div style={{ maxWidth: 1200, margin: "0 auto", padding: "22px 20px 40px" }}>
        <div>
          <div>
            <h1 style={{ fontSize: 21, fontWeight: 800, color: "#111827", margin: 0 }}>📝 Registro delle mie note</h1>
            <div style={{ fontSize: 13, color: "#6b7280", marginTop: 3 }}>
              Le note di tutte le commesse e le note generali{conTesto ? ` · ${conTesto} ${conTesto === 1 ? "nota" : "note"}` : ""}
            </div>
          </div>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 12, color: "#5b21b6", background: "#f5f3ff",
            border: "1px solid #ddd6fe", borderRadius: 8, padding: "5px 10px", marginTop: 10 }}>
            <Lock size={13} /> Visibili solo a te: nessun altro utente può leggerle, nemmeno il titolare.
          </div>
        </div>

        {/* Nuova nota: si sceglie sempre dove scriverla */}
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginTop: 16, padding: "12px 14px", background: "#fff",
          border: "1px solid #e5e7eb", borderRadius: 10 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: "#374151" }}>Nuova nota in</span>
          <select value={doveScelto} onChange={e => setDove(e.target.value)} style={{ ...selStile, minWidth: 240, maxWidth: "100%" }}>
            <option value="">Scegli dove…</option>
            <option value={GENERALE}>📌 Note generali (nessuna commessa)</option>
            {commesseScrivibili.map(p => <option key={p.id} value={p.id}>{etichetta(p)}</option>)}
          </select>
          <button className="btn btn-primary" onClick={nuova} disabled={!doveScelto} style={{ display: "flex", alignItems: "center", gap: 5 }}>
            <Plus size={14} /> Nuova nota
          </button>
        </div>

        {/* Filtri */}
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginTop: 12 }}>
          <select value={filtro} onChange={e => setFiltro(e.target.value)} style={{ ...selStile, maxWidth: "100%" }}>
            <option value="">Tutte, divise per commessa</option>
            <option value={GENERALE}>📌 Solo note generali</option>
            {opzioniFiltro.map(o => <option key={o.k} value={o.k}>{o.nome}</option>)}
          </select>
          <div style={{ position: "relative", flex: "1 1 220px", maxWidth: 360 }}>
            <Search size={14} style={{ position: "absolute", left: 10, top: 10, color: "#9ca3af" }} />
            <input value={cerca} onChange={e => setCerca(e.target.value)} placeholder="Cerca nelle note…" className="input" style={{ paddingLeft: 30, width: "100%" }} />
          </div>
          <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: "#374151", cursor: "pointer" }}>
            <input type="checkbox" checked={vediArchiviate} onChange={e => setVediArchiviate(e.target.checked)} /> Mostra anche le fatte / archiviate
          </label>
        </div>

        {caricate && gruppi.length === 0 && (
          <div style={{ textAlign: "center", color: "#9ca3af", fontSize: 13, padding: "50px 10px", lineHeight: 1.7 }}>
            {note.length === 0
              ? <>Non hai ancora nessuna nota.<br />Scegli sopra dove scriverla e premi «Nuova nota», oppure usa «📝 Le mie note» dentro una commessa.</>
              : "Nessuna nota corrisponde ai filtri."}
          </div>
        )}

        {gruppi.map(g => {
          const stato = statoGruppo(g.chiave);
          const apribile = g.chiave !== GENERALE && !!perId[g.chiave];
          return (
            <div key={g.chiave} style={{ marginTop: 22 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", paddingBottom: 8, borderBottom: "1px solid #e5e7eb", marginBottom: 12 }}>
                <span style={{ fontSize: 14.5, fontWeight: 800, color: "#1e1b4b" }}>{nomeGruppo(g.chiave, g.note[0])}</span>
                <span style={{ fontSize: 12, color: "#9ca3af" }}>{g.note.length} {g.note.length === 1 ? "nota" : "note"}</span>
                {stato && <span style={{ fontSize: 11, fontWeight: 700, color: "#92400e", background: "#fef3c7", borderRadius: 6, padding: "1px 7px" }}>{stato}</span>}
                {apribile && onNavigate && (
                  <button onClick={() => onNavigate({ page: "project", projectId: g.chiave })}
                    style={{ marginLeft: "auto", background: "none", border: "none", cursor: "pointer", fontSize: 12, fontWeight: 600, color: "#7c3aed", fontFamily: "'Inter',sans-serif" }}>
                    Apri commessa →
                  </button>
                )}
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: "0 14px", alignItems: "start" }}>
                {g.note.map(n => (
                  <PostIt key={n.id + (n.id === nuovaId ? "-n" : "")} nota={n} autoFocus={n.id === nuovaId}
                    onSalva={salvaTesto} onAggiorna={aggiorna} onElimina={elimina}
                    sotto={
                      <select value="" onChange={e => sposta(n, e.target.value)} title="Sposta la nota in un'altra commessa o tra le note generali"
                        style={{ marginTop: 4, width: "100%", fontSize: 11, color: "#6b7280", background: "rgba(255,255,255,.55)", border: "1px solid rgba(0,0,0,.08)",
                          borderRadius: 6, padding: "3px 6px", cursor: "pointer", fontFamily: "'Inter',sans-serif" }}>
                        <option value="">Sposta in…</option>
                        {chiaveDi(n) !== GENERALE && <option value={GENERALE}>📌 Note generali</option>}
                        {commesseScrivibili.filter(p => p.id !== n.projectId).map(p => <option key={p.id} value={p.id}>{etichetta(p)}</option>)}
                      </select>
                    } />
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function nomeGruppoDi(chiave, campione, perId) {
  if (chiave === GENERALE) return "📌 Note generali";
  if (perId[chiave]) return etichetta(perId[chiave]);
  return [campione?.codiceCommessa, campione?.projectName].filter(Boolean).join(" · ") || "Commessa";
}

function etichetta(p) {
  return [p.codiceCommessa, p.name].filter(Boolean).join(" · ") || "Commessa";
}
